import { createRequire } from "node:module";
import { existsSync, readFileSync } from "fs";
import { dirname, join, resolve } from "path";
import { resolvePackageDirFrom } from "./config-package-identity.ts";
import { getHomeDir, normalizePath } from "./utils/paths.ts";
import { isSplitLauncherRuntime, moduleFileFromMetaUrl } from "./utils/split-launcher.ts";
import { stripBom } from "./utils/text.ts";

// =============================================================================
// Package Detection
// =============================================================================

const __filename = moduleFileFromMetaUrl(import.meta.url, "app.js");
const __dirname = dirname(__filename);

/**
 * Detect if we're running as a Bun compiled binary.
 * Bun binaries have import.meta.url containing "$bunfs", "~BUN", or "%7EBUN" (Bun's virtual filesystem path).
 * Split Windows/macOS/Linux release launchers also set ATOMIC_CODING_AGENT before importing the sidecar app bundle.
 */
const bunFsMarkers = ["$bunfs", "~BUN", "%7EBUN"];
// Check process.argv[1] as well as import.meta.url: in a CJS (bytecode) bundle
// import.meta.url is rewritten to the original source path, but argv[1] still
// points into Bun's virtual filesystem.
export const isBunBinary =
	isSplitLauncherRuntime() ||
	[import.meta.url, process.argv[1] ?? ""].some((candidate) =>
		bunFsMarkers.some((marker) => candidate.includes(marker)),
	);

/**
 * Detect if we're running from a single-file bundle produced by `bun run bundle:dev`.
 * The bundle build inlines this env var via `--define`, so it is a compile-time
 * constant there and undefined everywhere else.
 */
export const isBundledBuild = process.env.ATOMIC_BUNDLED_BUILD === "1";

/** Detect if Bun is the runtime (compiled binary or bun run) */
export const isBunRuntime = !!process.versions.bun;

// =============================================================================
// Install Method Detection
// =============================================================================

import {
	detectInstallMethodForRuntime,
	getSelfUpdateCommandForRuntime,
	getSelfUpdateUnavailableInstructionForRuntime,
	getUpdateInstructionForRuntime,
	type SelfUpdateRuntime,
} from "./config-self-update.ts";

export type { InstallMethod, SelfUpdateCommand, SelfUpdateTarget } from "./config-self-update.ts";

function selfUpdateRuntime(): SelfUpdateRuntime {
	return {
		isBunBinary,
		isBunRuntime,
		moduleDir: __dirname,
		getPackageDir,
	};
}

export function detectInstallMethod(): import("./config-self-update.ts").InstallMethod {
	return detectInstallMethodForRuntime(selfUpdateRuntime());
}

export function getSelfUpdateCommand(
	packageName: string,
	npmCommand?: string[],
	updateTarget: import("./config-self-update.ts").SelfUpdateTarget = { packageName, installSpec: packageName },
): import("./config-self-update.ts").SelfUpdateCommand | undefined {
	return getSelfUpdateCommandForRuntime(selfUpdateRuntime(), packageName, npmCommand, updateTarget);
}

export function getSelfUpdateUnavailableInstruction(
	packageName: string,
	npmCommand?: string[],
	updateTarget: import("./config-self-update.ts").SelfUpdateTarget = { packageName, installSpec: packageName },
): string {
	return getSelfUpdateUnavailableInstructionForRuntime(selfUpdateRuntime(), packageName, npmCommand, updateTarget);
}

export function getUpdateInstruction(packageName: string): string {
	return getUpdateInstructionForRuntime(selfUpdateRuntime(), packageName);
}

// =============================================================================
// Package Asset Paths (shipped with executable)
// =============================================================================

/**
 * Get the base directory for resolving package assets (themes, package.json, README.md, CHANGELOG.md).
 * - For Bun binary: returns the directory containing the executable
 * - For Node.js (dist/): returns __dirname (the dist/ directory)
 * - For source (src/): returns parent directory (the package root)
 */
export function getPackageDir(): string {
	// Allow override via environment variable (useful for Nix/Guix where store paths tokenize poorly).
	// This runs before package.json app config is read, so the env var name is hardcoded.
	const envDir = process.env.ATOMIC_PACKAGE_DIR ?? process.env.PI_PACKAGE_DIR;
	if (envDir) {
		return normalizePath(envDir);
	}

	if (isBunBinary) {
		// Bun binary: process.execPath points to the compiled executable
		return dirname(process.execPath);
	}
	// Node.js: walk up from __dirname to the Atomic app package. Prebundled
	// companion extensions ship under dist/builtin/<name>/ with their own
	// package.json; stopping at the first one made APP_NAME "workflows".
	return resolvePackageDirFrom(__dirname);
}

/**
 * Resolve the root that contains source-layout assets for non-binary runtimes.
 * Ordinary source and compiled modules already live in src/ or dist/, so use
 * their module directory directly. Bundled development builds and explicit
 * package-directory overrides instead need to select a package-root layout.
 */
function getModuleAssetRoot(): string {
	const hasPackageDirOverride = !!(process.env.ATOMIC_PACKAGE_DIR || process.env.PI_PACKAGE_DIR);
	if (!isBundledBuild && !hasPackageDirOverride) {
		return __dirname;
	}
	const packageDir = getPackageDir();
	return join(packageDir, existsSync(join(packageDir, "src")) ? "src" : "dist");
}

/** Codemode workers and wasm live beside Atomic's split/development bundle. */
let codemodeWorkerDataUrl: URL | undefined;
let quickJSWasmPath: string | undefined;

export function getCodemodeWorkerUrl(): URL | undefined {
	if (codemodeWorkerDataUrl) return codemodeWorkerDataUrl;
	const worker = isBunBinary
		? join(getPackageDir(), "codemode-worker.js")
		: isBundledBuild
			? join(dirname(resolve(process.argv[1])), "codemode-worker.js")
			: join(getModuleAssetRoot(), "codemode-worker.js");
	if (!isBunBinary && !isBundledBuild && !existsSync(worker)) return undefined;
	// A self-contained worker survives replacement or removal of the running install.
	codemodeWorkerDataUrl ??= new URL(`data:text/javascript;base64,${readFileSync(worker).toString("base64")}`);
	return codemodeWorkerDataUrl;
}

export function getQuickJSWasmPath(): string {
	quickJSWasmPath ??= isBunBinary
		? join(getPackageDir(), "quickjs.wasm")
		: isBundledBuild
			? join(dirname(resolve(process.argv[1])), "quickjs.wasm")
			: createRequire(__filename).resolve("quickjs-wasi/quickjs.wasm");
	return quickJSWasmPath;
}

export type InstallChange = { kind: "updated"; version: string } | { kind: "removed" };

export function detectInstallChange(packageJsonPath = startupPackageJsonPath): InstallChange | undefined {
	if (isBunBinary || !packageJsonPath) return undefined;
	let installed: PackageJson;
	try {
		installed = JSON.parse(stripBom(readFileSync(packageJsonPath, "utf-8"))) as PackageJson;
	} catch (error) {
		return (error as NodeJS.ErrnoException).code === "ENOENT" ? { kind: "removed" } : undefined;
	}
	return installed.version && installed.version !== VERSION
		? { kind: "updated", version: installed.version }
		: undefined;
}
/**
 * Get path to built-in themes directory (shipped with package)
 * - For Bun binary: theme/ next to executable
 * - For Node.js (dist/): dist/modes/interactive/theme/
 * - For source (src/): src/modes/interactive/theme/
 */
export function getThemesDir(): string {
	if (isBunBinary) {
		return join(getPackageDir(), "theme");
	}
	return join(getModuleAssetRoot(), "modes", "interactive", "theme");
}

/**
 * Get path to HTML export template directory (shipped with package)
 * - For Bun binary: export-html/ next to executable
 * - For Node.js (dist/): dist/core/export-html/
 * - For source (src/): src/core/export-html/
 */
export function getExportTemplateDir(): string {
	if (isBunBinary) {
		return join(getPackageDir(), "export-html");
	}
	return join(getModuleAssetRoot(), "core", "export-html");
}

/** Get path to package.json */
export function getPackageJsonPath(): string {
	return join(getPackageDir(), "package.json");
}

/** Get path to README.md */
export function getReadmePath(): string {
	return resolve(join(getPackageDir(), "README.md"));
}

/** Get path to docs directory */
export function getDocsPath(): string {
	return resolve(join(getPackageDir(), "docs"));
}

/** Get path to examples directory */
export function getExamplesPath(): string {
	return resolve(join(getPackageDir(), "examples"));
}

/** Get path to CHANGELOG.md */
export function getChangelogPath(): string {
	return resolve(join(getPackageDir(), "CHANGELOG.md"));
}

/**
 * Get path to built-in interactive assets directory.
 * - For Bun binary: assets/ next to executable
 * - For Node.js (dist/): dist/modes/interactive/assets/
 * - For source (src/): src/modes/interactive/assets/
 */
export function getInteractiveAssetsDir(): string {
	if (isBunBinary) {
		return join(getPackageDir(), "assets");
	}
	return join(getModuleAssetRoot(), "modes", "interactive", "assets");
}

/** Get path to a bundled interactive asset */
export function getBundledInteractiveAssetPath(name: string): string {
	return join(getInteractiveAssetsDir(), name);
}

// =============================================================================
// App Config (from package.json <appName>Config, with piConfig as a legacy shim)
// =============================================================================

interface AppConfig {
	name?: string;
	configDir?: string;
	changelogUrl?: string;
}

interface PackageJson extends Record<string, unknown> {
	name?: string;
	version?: string;
	piConfig?: AppConfig;
}

let pkg: PackageJson = {};
let startupPackageJsonPath: string | undefined;
try {
	const packageJsonPath = getPackageJsonPath();
	pkg = JSON.parse(stripBom(readFileSync(packageJsonPath, "utf-8"))) as PackageJson;
	startupPackageJsonPath = packageJsonPath;
} catch (e: unknown) {
	const err = e as NodeJS.ErrnoException;
	if (err.code !== "ENOENT") throw e;
}

function appNameFromPackageName(packageName: string | undefined): string | undefined {
	const localName = packageName?.split("/").pop()?.trim();
	return localName && localName.length > 0 ? localName : undefined;
}

function readAppConfig(packageJson: PackageJson, appName: string | undefined): AppConfig | undefined {
	if (appName) {
		const appConfig = packageJson[`${appName}Config`];
		if (appConfig && typeof appConfig === "object" && !Array.isArray(appConfig)) {
			return appConfig as AppConfig;
		}
	}
	return packageJson.piConfig;
}

export const PACKAGE_NAME: string = pkg.name || "@bastani/atomic";
const packageAppName = appNameFromPackageName(PACKAGE_NAME);
const appConfig = readAppConfig(pkg, packageAppName);
export const APP_NAME: string = appConfig?.name || packageAppName || "pi";
export const APP_TITLE: string = appConfig?.name !== undefined || APP_NAME !== "pi" ? APP_NAME : "π";
export const CONFIG_DIR_NAME: string = appConfig?.configDir || (APP_NAME === "pi" ? ".pi" : `.${APP_NAME}`);
export const LEGACY_CONFIG_DIR_NAME = ".pi";
export const CONFIG_DIR_NAMES: readonly string[] =
	CONFIG_DIR_NAME === LEGACY_CONFIG_DIR_NAME ? [CONFIG_DIR_NAME] : [CONFIG_DIR_NAME, LEGACY_CONFIG_DIR_NAME];
export const VERSION: string = pkg.version || "0.0.0";
export const CHANGELOG_URL: string | undefined = appConfig?.changelogUrl?.trim() || undefined;

const ENV_PREFIX = APP_NAME.toUpperCase();
export const LEGACY_ENV_PREFIX = "PI";

// e.g., ATOMIC_CODING_AGENT_DIR (with PI_CODING_AGENT_DIR as a compatibility alias)
export const ENV_AGENT_DIR = `${ENV_PREFIX}_CODING_AGENT_DIR`;
export const ENV_SESSION_DIR = `${ENV_PREFIX}_CODING_AGENT_SESSION_DIR`;
export const ENV_PACKAGE_DIR = `${ENV_PREFIX}_PACKAGE_DIR`;
export const ENV_OFFLINE = `${ENV_PREFIX}_OFFLINE`;
export const ENV_SKIP_VERSION_CHECK = `${ENV_PREFIX}_SKIP_VERSION_CHECK`;
export const ENV_STARTUP_BENCHMARK = `${ENV_PREFIX}_STARTUP_BENCHMARK`;
export const ENV_TELEMETRY = `${ENV_PREFIX}_TELEMETRY`;
export const ENV_SHARE_VIEWER_URL = `${ENV_PREFIX}_SHARE_VIEWER_URL`;
export const ENV_CLEAR_ON_SHRINK = `${ENV_PREFIX}_CLEAR_ON_SHRINK`;
export const ENV_HARDWARE_CURSOR = `${ENV_PREFIX}_HARDWARE_CURSOR`;
export const ENV_CLIPBOARD_OSC52 = `${ENV_PREFIX}_CLIPBOARD_OSC52`;
export const ENV_TIMING = `${ENV_PREFIX}_TIMING`;
export const ENV_HEAP_MB = `${ENV_PREFIX}_HEAP_MB`;
export const ENV_CODEMODE_MAX_WORKERS = `${ENV_PREFIX}_CODEMODE_MAX_WORKERS`;
export const WORKFLOW_STAGE_SUBAGENT_GUARD_ENV = `${ENV_PREFIX}_WORKFLOW_STAGE_SUBAGENT_GUARD`;
export function getEnvNames(name: string): string[] {
	if (ENV_PREFIX === LEGACY_ENV_PREFIX || !name.startsWith(`${ENV_PREFIX}_`)) return [name];
	return [name, `${LEGACY_ENV_PREFIX}_${name.slice(ENV_PREFIX.length + 1)}`];
}

export function getEnvValue(name: string): string | undefined {
	for (const candidate of getEnvNames(name)) {
		const value = process.env[candidate];
		if (value !== undefined) return value;
	}
	return undefined;
}

export function hasEnvValue(name: string): boolean {
	return getEnvValue(name) !== undefined;
}

export function setEnvValue(name: string, value: string): void {
	process.env[name] = value;
}

export function expandTildePath(path: string): string {
	return normalizePath(path);
}

const DEFAULT_SHARE_VIEWER_URL = "https://pi.dev/session/";

export const VERSION_ADOPTION_ENDPOINT =
	"https://atomic-version-adoption.bastani-atomic.workers.dev/v1/version-adoption";

/** Get the share viewer URL for a gist ID */
export function getShareViewerUrl(gistId: string): string {
	const baseUrl = getEnvValue(ENV_SHARE_VIEWER_URL) || DEFAULT_SHARE_VIEWER_URL;
	return `${baseUrl}#${gistId}`;
}

// =============================================================================
// User Config Paths (~/.atomic/agent/*)
// =============================================================================

/** Get the agent config directory (e.g., ~/.atomic/agent/) */
export function getAgentDir(): string {
	const envDir = getEnvValue(ENV_AGENT_DIR);
	if (envDir) {
		return expandTildePath(envDir);
	}
	return join(getHomeDir(), CONFIG_DIR_NAME, "agent");
}

/** Get the legacy pi agent config directory (e.g., ~/.pi/agent/) */
export function getLegacyAgentDir(): string {
	return join(getHomeDir(), LEGACY_CONFIG_DIR_NAME, "agent");
}

/** Get agent config directories in precedence order (primary first, then legacy). */
export function getAgentDirs(): string[] {
	const primary = getAgentDir();
	if (hasEnvValue(ENV_AGENT_DIR) || CONFIG_DIR_NAME === LEGACY_CONFIG_DIR_NAME) {
		return [primary];
	}
	const legacy = getLegacyAgentDir();
	return legacy === primary ? [primary] : [primary, legacy];
}

/** Get user config root directories in precedence order (primary first, then legacy). */
export function getUserConfigDirs(): string[] {
	return CONFIG_DIR_NAMES.map((name) => join(getHomeDir(), name));
}

/** Get project config directories in precedence order (primary first, then legacy). */
export function getProjectConfigDirs(cwd: string): string[] {
	return CONFIG_DIR_NAMES.map((name) => join(cwd, name));
}

/** Get a path inside every user config root directory. */
export function getUserConfigPaths(...segments: string[]): string[] {
	return getUserConfigDirs().map((dir) => join(dir, ...segments));
}

/** Get a path inside every agent config directory. */
export function getAgentConfigPaths(...segments: string[]): string[] {
	return getAgentDirs().map((dir) => join(dir, ...segments));
}

/** Get a path inside every project config directory. */
export function getProjectConfigPaths(cwd: string, ...segments: string[]): string[] {
	return getProjectConfigDirs(cwd).map((dir) => join(dir, ...segments));
}

/** Model config paths from highest to lowest precedence: Atomic project/global, then Pi project/global. */
export function getModelsConfigPaths(cwd: string, agentDir: string = getAgentDir(), includeProject = true): string[] {
	const globalPaths =
		agentDir === getAgentDir() ? getAgentConfigPaths("models.json") : [join(agentDir, "models.json")];
	if (!includeProject) return globalPaths;
	const projectPaths = getProjectConfigPaths(cwd, "models.json");
	const paths: string[] = [];
	for (let index = 0; index < Math.max(projectPaths.length, globalPaths.length); index++) {
		if (projectPaths[index]) paths.push(projectPaths[index]);
		if (globalPaths[index]) paths.push(globalPaths[index]);
	}
	return [...new Set(paths)];
}

/** Get path to user's custom themes directory */
export function getCustomThemesDir(): string {
	return join(getAgentDir(), "themes");
}

/** Get path to models.json */
export function getModelsPath(): string {
	return join(getAgentDir(), "models.json");
}

/** Get path to auth.json */
export function getAuthPath(): string {
	return join(getAgentDir(), "auth.json");
}

/** Get path to settings.json */
export function getSettingsPath(): string {
	return join(getAgentDir(), "settings.json");
}

/** Get path to tools directory */
export function getToolsDir(): string {
	return join(getAgentDir(), "tools");
}

/** Get path to managed binaries directory (fd, rg) */
export function getBinDir(): string {
	return join(getAgentDir(), "bin");
}

const EXTENSION_TRANSPILE_CACHE_FORMAT = "ts-source-specifiers";

/**
 * Get path to the extension transpile cache directory (jiti fsCache).
 * Scoped by version and transform format so neither a release upgrade nor a
 * loader transform change reads stale transpiled output.
 */
export function getExtensionTranspileCacheDir(): string {
	return join(getAgentDir(), "cache", "jiti", `${VERSION}+${EXTENSION_TRANSPILE_CACHE_FORMAT}`);
}

/** Get path to prompt templates directory */
export function getPromptsDir(): string {
	return join(getAgentDir(), "prompts");
}

/** Get path to sessions directory */
export function getSessionsDir(): string {
	return join(getAgentDir(), "sessions");
}

/** Get path to debug log file */
export function getDebugLogPath(): string {
	return join(getAgentDir(), `${APP_NAME}-debug.log`);
}
